import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DockIcon from '@mui/icons-material/Dock';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import PriorReportTextView from '../PriorReportTextView';

export default function PriorResultsCard({
  theme,
  showPriorReportText,
  selectedPriorStudy,
  onBackFromPriorReportText,
  onShowPdfFromTextView,
  priorStudiesSearchText,
  onPriorStudiesSearchChange,
  priorStudiesModalityFilter,
  onPriorStudiesModalityFilterChange,
  getUniqueModalities,
  getFilteredAndSortedPriorStudies,
  priorStudiesColumns,
  priorStudiesApiRef,
  getGridLocale,
  onRowDoubleClick,
  sidebarDetached = false,
  onToggleDetach,
  showDetachIcon = true,
  fillAvailable = false,
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  return (
    <Paper
      elevation={1}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(fillAvailable
          ? { flex: '1 1 0', minHeight: 0 }
          : { flex: expanded ? '1 1 0' : '0 0 auto', minHeight: 0 }),
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ color: 'primary.main' }}>
          {t('report.priorStudies')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {showDetachIcon && onToggleDetach && (
            <Tooltip title={sidebarDetached
              ? t('report.sidebar.dock', 'Andocken')
              : t('report.sidebar.detach', 'In separatem Fenster öffnen')}
            >
              <IconButton
                size="small"
                onClick={onToggleDetach}
                aria-label={sidebarDetached
                  ? t('report.sidebar.dock', 'Andocken')
                  : t('report.sidebar.detach', 'In separatem Fenster öffnen')}
              >
                {sidebarDetached
                  ? <DockIcon fontSize="small" sx={{ color: 'var(--rd-primary)' }} />
                  : <OpenInNewIcon fontSize="small" sx={{ color: 'var(--rd-primary)' }} />}
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => setExpanded((v) => !v)} aria-label={expanded ? 'collapse' : 'expand'}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      <Collapse
        in={expanded}
        unmountOnExit
        timeout="auto"
        sx={{
          flex: '1 1 0',
          minHeight: 0,
          overflow: 'hidden',
          '& .MuiCollapse-wrapper': { height: '100%' },
          '& .MuiCollapse-wrapperInner': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: 2,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {showPriorReportText ? (
            <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'auto' }}>
              <PriorReportTextView
                study={selectedPriorStudy}
                onBack={onBackFromPriorReportText}
                onShowPdf={onShowPdfFromTextView}
              />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2, display: 'flex', gap: 2, flexShrink: 0, alignItems: 'flex-end' }}>
                <TextField
                  size="small"
                  placeholder={t('approval.search.placeholder')}
                  value={priorStudiesSearchText}
                  onChange={onPriorStudiesSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="modality-filter-label">{t('approval.filter.modality')}</InputLabel>
                  <Select
                    labelId="modality-filter-label"
                    value={priorStudiesModalityFilter}
                    onChange={onPriorStudiesModalityFilterChange}
                    label={t('approval.filter.modality')}
                  >
                    <MenuItem value="all">{t('approval.filter.all')}</MenuItem>
                    {getUniqueModalities().map((modality) => (
                      <MenuItem key={modality} value={modality}>{modality}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
                <DataGrid
                  apiRef={priorStudiesApiRef}
                  rows={getFilteredAndSortedPriorStudies()}
                  columns={priorStudiesColumns}
                  getRowId={(row, index) => row.id || row.study_instance_uid || index}
                  localeText={getGridLocale()}
                  density="compact"
                  hideFooter
                  disableColumnFilter
                  disableColumnMenu={false}
                  disableColumnSelector
                  disableDensitySelector
                  disableRowSelectionOnClick
                  onRowDoubleClick={onRowDoubleClick}
                  sx={{
                    border: 'none',
                    height: '100%',
                    width: '100%',
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiDataGrid-main': {
                      backgroundColor: theme.palette.background.paper,
                      overflow: 'hidden',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      backgroundColor: theme.palette.background.paper,
                      overflow: 'auto !important',
                    },
                    '& .MuiDataGrid-row': {
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)',
                        cursor: 'pointer',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
